import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, PopoverVirtualElement, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { Calendar, Event, momentLocalizer, SlotInfo, View } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import { Fulfillment } from 'api/models/Fulfillment';
import { ApiService } from 'api/services/ApiService';
import { useZoom } from 'context/ZoomContext';
import { getCalendarColorPalette } from 'dashboard409a/hooks/useColorPalette';
import { CalendarHoverProvider } from './CalendarHoverContext';
import CalendarEventComponent from './components/CalendarEventComponent';
import VacationEventPopover from './components/VacationEventPopover';
import {
  CalendarEvent,
  CalendarSortEventsArgs,
  GoogleCalendarEvent,
  isWorkboardTaskCalendarEvent,
  ModelsCalendarProps,
  VacationCalendarEvent,
  VacationToggleCalendarEvent,
  WorkboardTaskApiEvent,
  WorkboardTaskCalendarEvent,
} from './ModelsCalendar.types';
import {
  CALENDAR_DATE_FORMAT,
  CALENDAR_EVENT_BASE_STYLES,
  CALENDAR_MESSAGES,
  CALENDAR_STYLES,
  CALENDAR_VIEWS,
  DEFAULT_CALENDAR_VIEW,
  MODELS_CALENDAR_EVENT_TEXT_COLORS,
  MODELS_CALENDAR_STATUS_TEXT_COLOR_MAP,
} from '../../common/constants/calendar';
import useGoogleCalendar from '../../hooks/useGoogleCalendar';
import useWorkboardTaskCalendarEvents from '../../hooks/useWorkboardTaskCalendarEvents';
import { ModelWithValuationData } from '../../services/models.types';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';

import './styles.scss';

const localizer = momentLocalizer(moment);
const COLLAPSED_VACATION_ROW_LIMIT = 2;

const isVacationCalendarEvent = (event: Event): event is VacationCalendarEvent =>
  (event as VacationCalendarEvent).isGoogleVacationEvent === true;

const isVacationToggleEvent = (event: Event): event is VacationToggleCalendarEvent =>
  (event as VacationToggleCalendarEvent).isVacationToggleEvent === true;

const isVacationRowEvent = (event: Event) => isVacationCalendarEvent(event) || isVacationToggleEvent(event);

const getRushBorderStyle = (
  isRush: boolean,
  rushBorderColor: string,
  defaultBorder: string
): { border: string; boxShadow?: string } => ({
  border: isRush ? `${rushBorderColor} 2px solid` : defaultBorder,
  boxShadow: isRush ? `0 0 0 2px ${rushBorderColor}40` : undefined,
});

const GROUP_SORT_PRIORITY: Record<string, number> = {
  vacation: 3,
  model: 2,
  'workboard-task': 1,
};

const getGroupPriority = (group?: string | null) => GROUP_SORT_PRIORITY[group ?? ''] ?? 0;

const getEventGroup = (event: Event) => {
  if (isVacationRowEvent(event)) {
    return 'vacation';
  }
  if (isWorkboardTaskCalendarEvent(event)) {
    return 'workboard-task';
  }
  return 'model';
};

const getWeekKey = (range?: Date[]) => range?.map(date => moment(date).format(CALENDAR_DATE_FORMAT)).join('|') ?? 'unknown-week';

const getVacationWeekKey = (date: Date) => {
  const anchor = moment(date);
  return getWeekKey([anchor.clone().startOf('week').toDate(), anchor.clone().endOf('week').toDate()]);
};

const buildVacationToggleEvent = (
  weekKey: string,
  weekAnchor: Date,
  hiddenCount: number,
  isExpanded: boolean
): VacationToggleCalendarEvent => ({
  title: isExpanded ? 'Show fewer vacations' : `${hiddenCount} more vacations`,
  start: weekAnchor,
  end: weekAnchor,
  resource: { weekKey },
  isVacationToggleEvent: true,
});

const buildDisplayVacationEvents = (
  vacations: VacationCalendarEvent[],
  expandedWeekKeys: Set<string>
): Array<VacationCalendarEvent | VacationToggleCalendarEvent> => {
  const byWeek = new Map<string, VacationCalendarEvent[]>();

  vacations.forEach(vacation => {
    const start = vacation.start as Date | undefined;
    if (!start) {
      return;
    }
    const weekKey = getVacationWeekKey(start);
    const weekVacations = byWeek.get(weekKey) ?? [];
    weekVacations.push(vacation);
    byWeek.set(weekKey, weekVacations);
  });

  const displayed: Array<VacationCalendarEvent | VacationToggleCalendarEvent> = [];

  byWeek.forEach((weekVacations, weekKey) => {
    const sorted = [...weekVacations].sort(
      (a, b) => moment(a.start as Date).valueOf() - moment(b.start as Date).valueOf()
    );
    const isExpanded = expandedWeekKeys.has(weekKey);
    const weekAnchor = moment(sorted[0]?.start as Date).clone().startOf('week').toDate();

    if (sorted.length <= COLLAPSED_VACATION_ROW_LIMIT) {
      displayed.push(...sorted);
      return;
    }

    if (isExpanded) {
      displayed.push(...sorted);
      displayed.push(buildVacationToggleEvent(weekKey, weekAnchor, 0, true));
      return;
    }

    displayed.push(...sorted.slice(0, COLLAPSED_VACATION_ROW_LIMIT));
    displayed.push(buildVacationToggleEvent(weekKey, weekAnchor, sorted.length - COLLAPSED_VACATION_ROW_LIMIT, false));
  });

  return displayed;
};

const getVacationOriginalStart = (evt: CalendarSortEventsArgs['evtA']): string | null => {
  const {resource} = (evt as { resource?: GoogleCalendarEvent });
  return resource?.start?.date ?? resource?.start?.dateTime ?? null;
};

const sortCalendarEvents = ({ evtA, evtB }: CalendarSortEventsArgs) => {
  const prioritySort = getGroupPriority(evtB.group) - getGroupPriority(evtA.group);

  if (prioritySort !== 0) {
    return prioritySort;
  }

  const startDaySort = moment(evtA.start).startOf('day').valueOf() - moment(evtB.start).startOf('day').valueOf();

  if (startDaySort !== 0) {
    return startDaySort;
  }

  // When segment start days are equal (e.g. both clipped to week start for continuing events),
  // use the original Google Calendar start date to preserve consistent row ordering across weeks.
  if (evtA.group === 'vacation' && evtB.group === 'vacation') {
    const origA = getVacationOriginalStart(evtA);
    const origB = getVacationOriginalStart(evtB);
    if (origA && origB && origA !== origB) {
      return origA < origB ? -1 : 1;
    }
  }

  const durationSort = moment(evtB.end).diff(evtB.start, 'days') - moment(evtA.end).diff(evtA.start, 'days');

  if (durationSort !== 0) {
    return durationSort;
  }

  const startTimeSort = Number(evtA.start) - Number(evtB.start);

  if (startTimeSort !== 0) {
    return startTimeSort;
  }

  return Number(evtA.end) - Number(evtB.end);
};

const formatVacationEventTitle = (summary?: string): string => {
  const cleanSummary = summary?.trim();

  if (!cleanSummary) {
    return 'OOO Vacations';
  }

  if (/ooo/i.test(cleanSummary) || /vacation/i.test(cleanSummary)) {
    return cleanSummary;
  }

  return `${cleanSummary} - OOO Vacations`;
};

const getGoogleEventDate = (
  googleDate?: { date?: string; dateTime?: string }
): Date | null => {
  const value = googleDate?.date ?? googleDate?.dateTime;

  if (!value) {
    return null;
  }

  const parsed = googleDate?.date
    ? moment(value, 'YYYY-MM-DD', true)
    : moment(value);

  return parsed.isValid() ? parsed.toDate() : null;
};

// Type assertion needed because withDragAndDrop doesn't preserve generic types
// We know our events are CalendarEvent, so we can safely assert the types
// Using Record<string, unknown> to allow additional drag-and-drop props
const DragAndDropCalendar = withDragAndDrop(Calendar) as React.ComponentType<
  React.ComponentProps<typeof Calendar> & {
    onEventDrop?: (args: { event: CalendarEvent; start: Date; end: Date }) => void;
    draggableAccessor?: (event: Event) => boolean;
    sortEvents?: (args: CalendarSortEventsArgs) => number;
    eventGroupAccessor?: (event: Event) => string;
  } & Record<string, unknown>
>;

const ModelsCalendar = ({
  viewUserIds,
  models,
  isLoading,
  onSelectEvent,
  onOpenPreview,
  onEventDrop,
  hideToolbar = false,
  controlledDate,
  controlledView,
  onDateChange,
  onViewChange: onViewChangeProp,
  showGoogleVacationEvents = true,
  onDayDoubleClick,
  onWorkboardTaskSelect,
}: ModelsCalendarProps) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { zoomLevel } = useZoom();
  const [updatingEvents, setUpdatingEvents] = useState<Set<number>>(new Set());
  const [expandedVacationWeekKeys, setExpandedVacationWeekKeys] = useState<Set<string>>(new Set());
  const calendarRef = useRef<HTMLDivElement>(null);
  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [currentView, setCurrentView] = useState<View>(DEFAULT_CALENDAR_VIEW);

  const [vacationCalendarEvents, setVacationCalendarEvents] = useState<VacationCalendarEvent[]>([]);
  const [isVacationEventsLoading, setIsVacationEventsLoading] = useState(showGoogleVacationEvents);
  const [vacationPopover, setVacationPopover] = useState<{ event: VacationCalendarEvent; x: number; y: number } | null>(null);

  const vacationPopoverAnchor = useMemo((): PopoverVirtualElement | null => {
    if (!vacationPopover) {
      return null;
    }
    const { x, y } = vacationPopover;
    return {
      nodeType: 1,
      getBoundingClientRect: (): DOMRect => ({
        top: y, left: x, bottom: y, right: x, width: 0, height: 0, x, y, toJSON: () => undefined,
      }),
    };
  }, [vacationPopover]);

  const { getCalendarEvents } = useGoogleCalendar();

  // Use controlled values if provided, otherwise use internal state
  const activeDate = controlledDate ?? currentDate;
  const activeView = controlledView ?? currentView;

  const visibleDateRange = useMemo(() => {
    const anchor = moment(activeDate);
    if (activeView === 'week') {
      return {
        start: anchor.clone().startOf('week'),
        end: anchor.clone().endOf('week'),
      };
    }

    // Month view renders a full grid (including spillover days from adjacent weeks),
    // so include the full displayed range rather than strict month boundaries.
    return {
      start: anchor.clone().startOf('month').startOf('week'),
      end: anchor.clone().endOf('month').endOf('week'),
    };
  }, [activeDate, activeView]);

  const { data: workboardTaskApiEvents = [], isLoading: isWorkboardTaskEventsLoading } = useWorkboardTaskCalendarEvents(
    visibleDateRange.start.toISOString(),
    visibleDateRange.end.toISOString(),
    viewUserIds
  );

  const workboardTaskEvents = useMemo(
    () =>
      workboardTaskApiEvents
        .map((apiEvent: WorkboardTaskApiEvent) => {
          const startDate = moment(apiEvent.start.date, 'YYYY-MM-DD', true);

          if (!startDate.isValid()) {
            return null;
          }

          return {
            title: apiEvent.summary,
            start: startDate.toDate(),
            end: startDate.toDate(),
            resource: apiEvent.resource,
            isWorkboardTaskEvent: true,
          } as WorkboardTaskCalendarEvent;
        })
        .filter((event): event is WorkboardTaskCalendarEvent => event !== null),
    [workboardTaskApiEvents]
  );

  // Convert models to calendar events
  const events: CalendarEvent[] = useMemo(
    () =>
      models
        .filter(model => {
          // Only include models with a valid due date and id
          // supporting_due_date comes from Fulfillment type
          const dueDate = model.supporting_due_date;
          return model.id !== undefined && dueDate && dueDate !== '-' && dueDate.trim();
        })
        .map(model => {
          const dueDate = model.supporting_due_date as string;
          const date = moment(dueDate, CALENDAR_DATE_FORMAT);

          // Skip invalid dates
          if (!date.isValid()) {
            return null;
          }

          if (date.isBefore(visibleDateRange.start) || date.isAfter(visibleDateRange.end)) {
            return null;
          }

          // Create event title with company name only (no status)
          const companyName = model.company_name || CALENDAR_MESSAGES.UNKNOWN_COMPANY;
          const title = companyName;

          return {
            title,
            start: date.toDate(),
            end: date.toDate(),
            resource: model,
          } as CalendarEvent;
        })
        .filter((event): event is CalendarEvent => event !== null),
    [models, visibleDateRange]
  );

  const toggleVacationWeek = useCallback((weekKey: string) => {
    setExpandedVacationWeekKeys(prev => {
      const next = new Set(prev);

      if (next.has(weekKey)) {
        next.delete(weekKey);
      } else {
        next.add(weekKey);
      }

      return next;
    });
  }, []);

  const displayedVacationEvents = useMemo(
    () => buildDisplayVacationEvents(vacationCalendarEvents, expandedVacationWeekKeys),
    [vacationCalendarEvents, expandedVacationWeekKeys]
  );

  const handleSelectEvent = (event: Event, e?: React.SyntheticEvent) => {
    if (isVacationToggleEvent(event)) {
      toggleVacationWeek(event.resource.weekKey);
      return;
    }

    if (isVacationCalendarEvent(event)) {
      const mouseEvent = e as React.MouseEvent;
      setVacationPopover({ event, x: mouseEvent.clientX, y: mouseEvent.clientY });
      return;
    }

    if (isWorkboardTaskCalendarEvent(event)) {
      onWorkboardTaskSelect?.(event.resource.workboard_task_id, event.resource.occurrence_date);
      return;
    }

    const calendarEvent = event as CalendarEvent;
    const model = calendarEvent.resource;

    if (onOpenPreview) {
      onOpenPreview(model);
    } else if (onSelectEvent) {
      onSelectEvent(model);
    }
  };

  const handleNavigate = (newDate: Date) => {
    if (onDateChange) {
      onDateChange(newDate);
    } else {
      setCurrentDate(newDate);
    }
  };

  const handleViewChange = (newView: View) => {
    if (onViewChangeProp) {
      onViewChangeProp(newView);
    } else {
      setCurrentView(newView);
    }
  };

  // Helper function to validate model fields
  const validateModelFields = useCallback(
    (model: ModelWithValuationData): number | null => {
      if (!model.company_id) {
        enqueueSnackbar('Company ID is required to update dates.', { variant: 'error' });
        return null;
      }

      if (!model.attio_valuation_id) {
        enqueueSnackbar('Valuation ID is required to update dates.', { variant: 'error' });
        return null;
      }

      const fulfillmentId = model.id;
      if (fulfillmentId === undefined) {
        enqueueSnackbar('Model ID is required to update dates.', { variant: 'error' });
        return null;
      }

      return fulfillmentId;
    },
    [enqueueSnackbar]
  );

  // Helper function to validate and format dates
  const validateAndFormatDates = useCallback(
    (
      originalDateString: string | null | undefined,
      newDate: Date
    ): { newDateString: string; originalDateString: string } | null => {
      if (!originalDateString) {
        enqueueSnackbar('Original due date is missing.', { variant: 'error' });
        return null;
      }

      const originalDate = moment(originalDateString, CALENDAR_DATE_FORMAT);
      if (!originalDate.isValid()) {
        enqueueSnackbar('Invalid original date format.', { variant: 'error' });
        return null;
      }

      const newMomentDate = moment(newDate);
      if (!newMomentDate.isValid()) {
        enqueueSnackbar('Invalid date from drag and drop.', { variant: 'error' });
        return null;
      }

      const formattedNewDate = newMomentDate.format(CALENDAR_DATE_FORMAT);
      const formattedOriginalDate = originalDate.format(CALENDAR_DATE_FORMAT);

      if (formattedOriginalDate === formattedNewDate) {
        return null; // No change needed
      }

      return { newDateString: formattedNewDate, originalDateString: formattedOriginalDate };
    },
    [enqueueSnackbar]
  );

  // Helper function to extract error message
  const getErrorMessage = useCallback((error: unknown): string => {
    if (error && typeof error === 'object' && 'response' in error) {
      const apiError = error as { response?: { status?: number; data?: { message?: string } }; message?: string };
      if (apiError.response?.status === 404) {
        return 'Model not found or you do not have access.';
      }
      if (apiError.response?.status === 400) {
        return apiError.response?.data?.message || 'Invalid date format.';
      }
      if (apiError.message) {
        return apiError.message;
      }
    }
    if (error instanceof Error) {
      return error.message;
    }
    return 'Failed to update date. Please try again.';
  }, []);

  // Helper function to perform optimistic update
  const performOptimisticUpdate = useCallback(
    (modelId: number, newDateString: string) => {
      if (onEventDrop) {
        onEventDrop(modelId, newDateString);
      }
      setUpdatingEvents(prev => new Set(prev).add(modelId));
    },
    [onEventDrop]
  );

  // Helper function to update API and handle success
  const updateDueDateApi = useCallback(
    async (model: ModelWithValuationData, newDateString: string, newDate: Date) => {
      const updateData: Partial<Fulfillment> = {
        supporting_due_date: newDateString,
      };

      await ApiService.apiDashboardCompanyOperationsFulfillmentPartialUpdate(
        model.company_id.toString(),
        model.attio_valuation_id,
        updateData as Fulfillment
      );

      enqueueSnackbar(`Due Date updated to ${moment(newDate).format('MMM DD, YYYY')}`, {
        variant: 'success',
      });
    },
    [enqueueSnackbar]
  );

  // Helper function to handle error and revert optimistic update
  const handleUpdateError = useCallback(
    (error: unknown, modelId: number, originalDateString: string) => {
      if (onEventDrop) {
        onEventDrop(modelId, originalDateString);
      }

      const errorMessage = getErrorMessage(error);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    },
    [onEventDrop, getErrorMessage, enqueueSnackbar]
  );

  // Helper function to clean up updating state
  const cleanupUpdatingState = useCallback((modelId: number) => {
    setUpdatingEvents(prev => {
      const newSet = new Set(prev);
      newSet.delete(modelId);
      return newSet;
    });
  }, []);

  const handleEventDrop = useCallback(
    async ({ event, start }: { event: Event; start: Date; end: Date }) => {
      if (isVacationCalendarEvent(event)) {
        return;
      }

      if (isWorkboardTaskCalendarEvent(event)) {
        return;
      }

      const calendarEvent = event as CalendarEvent;
      const model = calendarEvent.resource;

      const modelId = validateModelFields(model);
      if (modelId === null) {
        return;
      }

      const dateValidation = validateAndFormatDates(model.supporting_due_date, start);
      if (!dateValidation) {
        return;
      }

      const { newDateString, originalDateString } = dateValidation;

      performOptimisticUpdate(modelId, newDateString);

      try {
        await updateDueDateApi(model, newDateString, start);
      } catch (error: unknown) {
        handleUpdateError(error, modelId, originalDateString);
      } finally {
        cleanupUpdatingState(modelId);
      }
    },
    [
      validateModelFields,
      validateAndFormatDates,
      performOptimisticUpdate,
      updateDueDateApi,
      handleUpdateError,
      cleanupUpdatingState,
    ]
  );

  const eventStyleGetter = (event: Event) => {
    if (isVacationToggleEvent(event)) {
      return {
        className: 'models-calendar-vacation-event models-calendar-vacation-toggle-event',
        style: CALENDAR_EVENT_BASE_STYLES,
      };
    }

    if (isVacationCalendarEvent(event)) {
      const vacationBackgroundColor = theme.palette.mode === 'dark' ? theme.palette.info.dark : theme.palette.info.light;
      const vacationBorderColor = theme.palette.info.main;
      const vacationTextColor = theme.palette.getContrastText(vacationBackgroundColor);

      return {
        className: 'models-calendar-vacation-event',
        style: {
          ...CALENDAR_EVENT_BASE_STYLES,
          backgroundColor: vacationBackgroundColor,
          border: `${vacationBorderColor} 1px solid`,
          color: vacationTextColor,
        },
      };
    }

    const rushBorderColor = theme.palette.error.main;

    if (isWorkboardTaskCalendarEvent(event)) {
      const isRush = event.resource.is_rush ?? false;
      return {
        className: 'models-calendar-workboard-task-event',
        style: {
          ...CALENDAR_EVENT_BASE_STYLES,
          ...getRushBorderStyle(isRush, rushBorderColor, CALENDAR_EVENT_BASE_STYLES.border),
        },
      };
    }

    // Type guard to ensure event is CalendarEvent
    const calendarEvent = event as CalendarEvent;
    const status = calendarEvent.resource.model_status;
    const modelId = calendarEvent.resource.id;
    const isRushDeal = calendarEvent.resource.is_rush_deal ?? false;

    // Type guard: ensure status and id exist
    const statusNumber: number = status ?? -1;
    const textColor = MODELS_CALENDAR_STATUS_TEXT_COLOR_MAP[statusNumber] || MODELS_CALENDAR_EVENT_TEXT_COLORS.DEFAULT;
    const isUpdating = modelId !== undefined && updatingEvents.has(modelId);
    const palette = getCalendarColorPalette();

    return {
      style: {
        ...CALENDAR_EVENT_BASE_STYLES,
        backgroundColor: palette.BACKGROUND,
        ...getRushBorderStyle(isRushDeal, rushBorderColor, `${palette.BORDER} 1px solid`),
        opacity: isUpdating ? 0.5 : CALENDAR_STYLES.EVENT_OPACITY,
        color: textColor,
      },
    };
  };

  useEffect(() => {
    if (!showGoogleVacationEvents) {
      setVacationCalendarEvents([]);
      setIsVacationEventsLoading(false);
      return;
    }

    setIsVacationEventsLoading(true);
    const fetchVacationCalendarEvents = async () => {
      const vacationCalendarEventsResponse = await getCalendarEvents(visibleDateRange.start.toISOString(), visibleDateRange.end.toISOString());

      const normalizedResponse = vacationCalendarEventsResponse as { events?: GoogleCalendarEvent[] } | GoogleCalendarEvent[];
      const rawEvents = Array.isArray(normalizedResponse) ? normalizedResponse : normalizedResponse.events;
      const vacationCalendarEventsMap: VacationCalendarEvent[] =
        rawEvents
          ?.map(event => {
            const startDate = getGoogleEventDate(event.start);
            const endDate = getGoogleEventDate(event.end) ?? startDate;

            if (!startDate || !endDate) {
              return null;
            }

            return {
              title: formatVacationEventTitle(event.summary),
              start: startDate,
              end: endDate,
              resource: event,
              isGoogleVacationEvent: true,
            } as VacationCalendarEvent;
          })
          .filter((event): event is VacationCalendarEvent => event !== null) ?? [];

      setVacationCalendarEvents(vacationCalendarEventsMap);
      setIsVacationEventsLoading(false);
    };
    fetchVacationCalendarEvents();
  }, [getCalendarEvents, showGoogleVacationEvents, visibleDateRange.start, visibleDateRange.end]);

  const handleSelectSlot = useCallback(
    (slotInfo: SlotInfo) => {
      if (slotInfo.action !== 'doubleClick' || !onDayDoubleClick) {
        return;
      }
      onDayDoubleClick(slotInfo.start);
    },
    [onDayDoubleClick]
  );

  // Fix for drag and drop with zoom: Intercept and adjust mouse coordinates
  useEffect(() => {
    if (!calendarRef.current || zoomLevel === 1) {
      return undefined;
    }



    const calendarElement = calendarRef.current;

    // Intercept mouse events during drag and drop to adjust coordinates for zoom
    const adjustCoordinates = (event: MouseEvent) => {
      // Check if we're in a drag operation
      const isDragging = document.querySelector('.rbc-addons-dnd-dragging') !== null;
      if (!isDragging) {
        return;
      }

      // Get bounding box - this is already affected by root zoom
      const rect = calendarElement.getBoundingClientRect();

      // Mouse coordinates are in the root zoom space
      // We need to convert them to the calendar's internal coordinate space
      // When root zoom is 0.8, everything is 0.8x smaller
      // The calendar container has inverse zoom (1/0.8 = 1.25) to cancel it out
      // So internally, the calendar thinks it's 1.25x larger
      // We need to adjust mouse coordinates to match this internal size

      const relativeX = (event.clientX - rect.left) / zoomLevel;
      const relativeY = (event.clientY - rect.top) / zoomLevel;

      // Convert back to client coordinates in the calendar's internal space
      const adjustedClientX = rect.left + relativeX;
      const adjustedClientY = rect.top + relativeY;

      // Override event properties
      try {
        Object.defineProperty(event, 'clientX', {
          writable: true,
          configurable: true,
          value: adjustedClientX,
        });
        Object.defineProperty(event, 'clientY', {
          writable: true,
          configurable: true,
          value: adjustedClientY,
        });
      } catch {
        // If we can't modify the event, the coordinates will be wrong
        // This is a limitation we'll have to work with
      }
    };

    // Intercept events in capture phase before react-big-calendar processes them
    calendarElement.addEventListener('mousemove', adjustCoordinates, { capture: true, passive: false });
    calendarElement.addEventListener('mouseup', adjustCoordinates, { capture: true, passive: false });

    return () => {
      calendarElement.removeEventListener('mousemove', adjustCoordinates, { capture: true });
      calendarElement.removeEventListener('mouseup', adjustCoordinates, { capture: true });
    };
  }, [zoomLevel]);

  // Calculate inverse zoom to cancel out root zoom for correct drag and drop coordinates
  const inverseZoom = zoomLevel === 0 ? 1 : 1 / zoomLevel;

  if (isLoading || isVacationEventsLoading || isWorkboardTaskEventsLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const visibleVacationEvents = showGoogleVacationEvents ? displayedVacationEvents : [];

  if (events.length === 0 && visibleVacationEvents.length === 0 && workboardTaskEvents.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography variant="body1" color="text.secondary">
          {CALENDAR_MESSAGES.NO_DUE_DATES}
        </Typography>
      </Box>
    );
  }

  return (
    <CalendarHoverProvider>
      <Box
        ref={calendarRef}
        sx={{
          display: 'flex',
          flex: 1,
          minHeight: 0,
          height: '100%',
          padding: 2,
          // Apply inverse zoom to cancel out root zoom for correct drag and drop coordinates
          zoom: inverseZoom,
          '--models-calendar-off-range-bg': theme.palette.mode === 'dark' ? '#2A2A2F' : '#f0f0f0',
          '--models-calendar-header-bg': theme.palette.mode === 'dark' ? '#111111' : '#f0f0f0',
          '--models-calendar-current-day-bg': theme.palette.mode === 'dark' ? '#777777' : '#f0f0f0',
          '--models-calendar-vacation-toggle-color': theme.palette.mode === 'dark' ? theme.palette.success.light : '#2e7d32',
          '--models-calendar-vacation-toggle-hover-bg': theme.palette.mode === 'dark' ? 'rgb(134 239 172 / 12%)' : 'rgb(46 125 50 / 12%)',
        }}
      >
        <DragAndDropCalendar
          localizer={localizer}
          events={[...events, ...visibleVacationEvents, ...workboardTaskEvents]}
          allDayAccessor={(event: Event) => isVacationRowEvent(event) || isWorkboardTaskCalendarEvent(event)}
          eventGroupAccessor={getEventGroup}
          startAccessor={(ev: unknown) => (ev as { start: Date }).start}
          endAccessor={(ev: unknown) => (ev as { end: Date }).end}
          date={activeDate}
          view={activeView}
          onNavigate={handleNavigate}
          onView={handleViewChange}
          toolbar={!hideToolbar}
          showAllEvents
          onSelectEvent={(event: Event, e: React.SyntheticEvent) => {
            handleSelectEvent(event, e);
          }}
          onEventDrop={handleEventDrop}
          selectable={Boolean(onDayDoubleClick)}
          onSelectSlot={handleSelectSlot}
          draggableAccessor={(event: Event) => !isVacationRowEvent(event) && !isWorkboardTaskCalendarEvent(event)}
          eventPropGetter={eventStyleGetter}
          sortEvents={sortCalendarEvents}
          components={{ event: CalendarEventComponent }}
          views={[...CALENDAR_VIEWS]}
          popup
          showMultiDayTimes={false}
        />
        <VacationEventPopover
          event={vacationPopover?.event ?? null}
          anchorEl={vacationPopoverAnchor}
          onClose={() => setVacationPopover(null)}
        />
      </Box>
    </CalendarHoverProvider>
  );
};

export default ModelsCalendar;
